import type { PictureBookStyle } from '@/types/manga';
import type { ReaderBand } from '@/utils/manga/comic-story-profile';
import { findCatalogStyle } from '@/lib/cover-styles';

/** Picture-book inner pages: appended to the cover model-sheet label (R12); the page itself holds no text. */
export const PICTURE_BOOK_COVER_LABEL_NOTE = 'Ignore any title lettering on the cover; this page has no text.';

/** The art styles a picture book can be drawn in by the comic engine, in display order. */
export const PICTURE_BOOK_STYLES: readonly PictureBookStyle[] = [
  'watercolor', 'european', 'impressionist', 'oil-painting', 'anime', 'graphicnovel', 'storybook',
];

/** A catalog id or display name -> picture-book style, or null when the catalog style is not offered. */
export function pictureBookStyleFromCatalog(styleIdOrName: string): PictureBookStyle | null {
  // 'european' is owned here: it is not in the public style catalog in this phase.
  if ((styleIdOrName || '').trim().toLowerCase() === 'european') return 'european';
  const entry = findCatalogStyle(styleIdOrName);
  return entry && (PICTURE_BOOK_STYLES as readonly string[]).includes(entry.id) ? (entry.id as PictureBookStyle) : null;
}

/** The catalog display name of a picture-book style. */
export function pictureBookStyleName(style: PictureBookStyle): string {
  if (style === 'european') return 'European';
  return findCatalogStyle(style)?.name ?? style;
}

/** Bands whose text is set on the page facing the picture (older readers) rather than under it. */
export function isFacingTextBand(band: ReaderBand | undefined): boolean {
  return band === 'O' || band === 'W';
}

/**
 * The styles the create flow offers. Storybook and European are held out (owner 2026-10-28 style test: same
 * kite book in three styles, Watercolor best, European worst likeness). The routes accept them only with the
 * internal key (e2e scripts). Books already drawn in a held-out style keep it.
 */
export const PICTURE_BOOK_FLOW_STYLES: readonly PictureBookStyle[] = [
  'watercolor', 'impressionist', 'oil-painting', 'anime', 'graphicnovel',
];

/** Suggested styles per reading band; the first entry is the default. Only flow styles are suggested. */
export const PICTURE_BOOK_BAND_STYLES: Record<ReaderBand, readonly PictureBookStyle[]> = {
  T: ['watercolor'],
  E: ['watercolor'],
  M: ['watercolor'],
  O: ['graphicnovel', 'anime'],
  W: ['anime', 'graphicnovel'],
  G: ['oil-painting', 'impressionist'],
};
